import React, { useContext, useEffect, useState } from "react";
import DeleteRowIcon from "../../../images/Builder/delete.svg";
import headerDeleteIcon from "../../../images/Builder/delete_bordered.svg";
import headerDeleteDarkIcon from "../../../images/Builder/delete_bordered_dark.svg";
import refreshIcon from "../../../images/Builder/refresh.svg";
import ExmptyBasket from "../../../components/EmptyBasket";
import ExpiryDropdown from "./ExpiryDropdown";
import { MoreHorizontal, Plus } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useBuilderContext } from "../context/BuilderContext";
import WebsocketWrapperContext from "../../../context/WebsocketWrapperContext";
import BasketBottom from "./BasketBottom";
import { CustomDropdown } from "./CustomDropdown";

const Basket = ({
  currentRows,
  handleAddNewRow,
  handleAddNewStrategyClick,
  charges,
  showConfirmModal,
  computedData,
  orderTypes,
  inputModes,
  onOrderTypesChange,
  onInputModesChange,
}) => {
  const {
    selectedSymbol,
    rows,
    setRows,
    selectedData,
    setSelectedData,
    allOptionsData,
    limitOrderStates,
    setLimitOrderStates,
    handleLotxChange,
    handleLotSizeChange,
  } = useBuilderContext();
  const { symbolData, subscribeToSymbols } = useContext(
    WebsocketWrapperContext
  );

  const { theme } = useTheme();

  //states
  const [rotate, setRotate] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null); // Track which dropdown is open

  useEffect(() => {
    const scrip = selectedSymbol?.scrip;

    if (
      selectedData &&
      scrip &&
      (!limitOrderStates?.[scrip] ||
        limitOrderStates[scrip].length !== selectedData.length)
    ) {
      setLimitOrderStates((prev) => {
        const updatedStates = selectedData.map((row) => row?.orderType === "L");

        return {
          ...prev,
          [scrip]: updatedStates,
        };
      });
    }
  }, [selectedData]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownOpen !== null &&
        !event.target.closest(".dropdown-container")
      ) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  //functions
  const handlePriceRefreshClick = () => {
    setRotate(true);
    let change = false;
    let updatedRows = selectedData.map((row) => {
      if (symbolData[row.code]) {
        row.price = symbolData[row.code]?.ltp;
        if (!row.price) {
          row.price = symbolData[row.code]?.prev_close;
        }
        change = true; // assuming `change` is defined outside this scope
      }
      return row;
    });
    if (change) {
      setRows((prev) => {
        return {
          ...prev,
          [selectedSymbol?.scrip]: updatedRows,
        };
      });
    }
    setTimeout(() => {
      setRotate(false);
    }, 500);
  };

  const handleDeleteAllRows = () => {
    if (selectedData?.length > 0) {
      setRows((prev) => ({
        ...prev,
        [selectedSymbol?.scrip]: [],
      }));
      setLimitOrderStates((prev) => ({
        ...prev,
        [selectedSymbol?.scrip]: [],
      }));
      // Clear order types and input modes when deleting all rows
      onOrderTypesChange?.({});
      onInputModesChange?.({});
    }
  };

  const triggerHandleAddNewRow = () => {
    handleAddNewRow();
  };

  // Handle dropdown menu toggle
  const toggleDropdown = (index) => {
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  // Handle order type change
  const handleOrderTypeChange = (index, type) => {
    const newOrderTypes = {
      ...orderTypes,
      [index]: type,
    };
    onOrderTypesChange?.(newOrderTypes);

    // Set rupee (absolute) as default for both stoploss and target
    const newInputModes = {
      ...inputModes,
      [index]: {
        stoploss: "absolute",
        ...(type === "bracket" && { target: "absolute" }),
      },
    };
    onInputModesChange?.(newInputModes);

    setDropdownOpen(null);
  };

  // Handle removing order type
  const handleRemoveOrderType = (index) => {
    // Remove from orderTypes
    const newOrderTypes = { ...orderTypes };
    delete newOrderTypes[index];
    onOrderTypesChange?.(newOrderTypes);

    // Clear the input modes for this index
    const newInputModes = { ...inputModes };
    delete newInputModes[index];
    onInputModesChange?.(newInputModes);

    // Clear the stoploss and target values
    const updatedData = [...selectedData];
    if (updatedData[index]) {
      updatedData[index] = {
        ...updatedData[index],
        stoploss: "",
        target: "",
      };
      setSelectedData(updatedData);
    }

    setDropdownOpen(null);
  };

  // Handle toggle button clicks for input modes
  const handleToggleInputMode = (index, field, mode) => {
    const newInputModes = {
      ...inputModes,
      [index]: {
        ...inputModes[index],
        [field]: mode,
      },
    };
    onInputModesChange?.(newInputModes); // Notify parent
  };

  // Handle stoploss change
  const handleStoplossChange = (index, value) => {
    const updatedData = [...selectedData];
    updatedData[index] = {
      ...updatedData[index],
      stoploss: value,
    };
    setSelectedData(updatedData);
  };

  // Handle target change
  const handleTargetChange = (index, value) => {
    const updatedData = [...selectedData];
    updatedData[index] = {
      ...updatedData[index],
      target: value,
    };
    setSelectedData(updatedData);
  };

  const handleDeleteRow = (index) => {
    if (!selectedData || selectedData.length === 0) return;

    const updatedRows = selectedData.filter((_, i) => i !== index);
    const updatedLimitStates = (
      limitOrderStates[selectedSymbol?.scrip] || []
    ).filter((_, i) => i !== index);

    setRows((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: updatedRows,
    }));
    setLimitOrderStates((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: updatedLimitStates,
    }));

    // Clean up orderTypes and inputModes for deleted row
    const newOrderTypes = { ...orderTypes };
    const newInputModes = { ...inputModes };

    // Remove the deleted index and shift remaining indices
    delete newOrderTypes[index];
    delete newInputModes[index];

    // Shift indices for rows after the deleted one
    const shiftedOrderTypes = {};
    const shiftedInputModes = {};

    Object.keys(newOrderTypes).forEach((key) => {
      const numKey = parseInt(key);
      if (numKey > index) {
        shiftedOrderTypes[numKey - 1] = newOrderTypes[key];
      } else {
        shiftedOrderTypes[key] = newOrderTypes[key];
      }
    });

    Object.keys(newInputModes).forEach((key) => {
      const numKey = parseInt(key);
      if (numKey > index) {
        shiftedInputModes[numKey - 1] = newInputModes[key];
      } else {
        shiftedInputModes[key] = newInputModes[key];
      }
    });

    onOrderTypesChange?.(shiftedOrderTypes);
    onInputModesChange?.(shiftedInputModes);
  };

  const toggleBuySell = (index) => {
    if (!selectedData || selectedData.length === 0) return;

    const updatedRows = selectedData.map((row, i) =>
      i === index ? { ...row, isBuy: !row.isBuy } : row
    );

    setRows((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: updatedRows,
    }));
  };

  const toggleCallPut = (ind) => {
    let tSymbol = selectedData[ind].trading_symbol;
    let tCode = selectedData[ind].code;
    let instrumentName = selectedData[ind].instrumentName;
    let newRow = selectedData[ind]?.allData?.find((o, i) => {
      if (o.strike == selectedData[ind].strike) {
        return o;
      }
    });
    if (newRow != undefined && newRow != null) {
      if (selectedData[ind].isCALL) {
        tSymbol = newRow.put.trading_symbol;
        tCode = newRow.put.code;
        instrumentName = newRow.put.instrument_name;
      } else {
        tSymbol = newRow.call.trading_symbol;
        tCode = newRow.call.code;
        instrumentName = newRow.call.instrument_name;
      }
    }
    const newstate = selectedData.map((obj, index) => {
      if (index == ind) {
        let f = obj.isCALL;
        const newobj = [tCode];
        subscribeToSymbols(newobj, newobj?.exchange);
        return {
          ...obj,
          isCALL: !f,
          tradingSymbol: tSymbol,
          code: tCode,
          instrumentName: instrumentName,
          price: null,
          iv: null,
        };
      }
      return obj;
    });
    setRows((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: newstate,
    }));
  };

  const handleExpiryChange = (ind, expiry) => {
    // console.log(ind, expiry);
    if (!selectedData || selectedData.length === 0) return;
    const optionsData = allOptionsData[selectedSymbol?.scrip][expiry];
    const allstr = extractStrikeValues(optionsData);
    const [tS, tC] = updateCodeSymbol(optionsData, ind);
    const newobj = [tC];
    subscribeToSymbols(newobj, optionsData.options[0].call?.exchange);
    const newste = selectedData.map((obj, index) => {
      if (index == ind) {
        return {
          ...obj,
          expiry: expiry,
          allstrks: allstr,
          tradingSymbol: tS,
          code: tC,
          allData: optionsData.options,
          price: null,
          iv: null,
        };
      }
      return obj;
    });
    setRows((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: newste,
    }));
  };

  const handleStrikeShift = async (ind, direction) => {
    let dat = selectedData[ind];
    let { strike, allstrks, allData, isCALL, tradingSymbol, code, expiry } =
      dat;
    // console.log(allstrks, strike);
    const currentIndex = allstrks.indexOf(String(strike));

    // console.log(currentIndex);
    if (direction === "prev" && currentIndex <= 0) return;
    if (direction === "next" && currentIndex >= allstrks.length - 1) return;

    let newIndex = direction === "prev" ? currentIndex - 1 : currentIndex + 1;

    const newStrike = allstrks[newIndex];
    const newRow = allData?.find((o) => o.strike === newStrike);

    let newSymbol = tradingSymbol;
    let newCode = code;

    if (newRow) {
      if (isCALL) {
        newSymbol = newRow.call?.trading_symbol || newSymbol;
        newCode = newRow.call?.code || newCode;
      } else {
        newSymbol = newRow.put?.trading_symbol || newSymbol;
        newCode = newRow.put?.code || newCode;
      }
    }

    const updatedRows = selectedData.map((row, idx) => {
      if (idx === ind) {
        subscribeToSymbols([newCode], newRow?.exchange);
        return {
          ...row,
          strike: parseInt(newStrike),
          tradingSymbol: newSymbol,
          code: newCode,
          price: null,
          iv: null,
        };
      }
      return row;
    });
    // console.log("here");
    setRows((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: updatedRows,
    }));
  };

  const handleToggleOrderType = (index) => {
    if (!selectedSymbol?.scrip || !selectedData || selectedData.length === 0)
      return;

    setLimitOrderStates((prev) => {
      const scrip = selectedSymbol.scrip;
      const currentStates =
        prev[scrip] || Array(selectedData.length).fill(false);

      const updatedStates = [...currentStates];
      updatedStates[index] = !updatedStates[index];

      return {
        ...prev,
        [scrip]: updatedStates,
      };
    });

    const updatedRows = selectedData.map((obj, ind) => {
      if (index === ind) {
        return { ...obj, orderType: obj.orderType == "L" ? "M" : "L" };
      }
      return obj;
    });
    setRows((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: updatedRows,
    }));
  };

  const handlePriceChange = (e, ind) => {
    const newPrice = e.target.value.trim() === "" ? "" : e.target.value;
    // // console.log("New price:", newPrice);

    const updatedRows = selectedData.map((obj, index) => {
      if (index === ind) {
        return { ...obj, price: newPrice };
      }
      return obj;
    });
    setRows((prev) => ({
      ...prev,
      [selectedSymbol?.scrip]: updatedRows,
    }));
  };

  //helpers
  const updateCodeSymbol = (optdt, ind) => {
    const selectedRow = selectedData[ind];
    let tSymbol = selectedData[ind].trading_symbol;
    let tCode = selectedData[ind].code;
    let newRow = optdt?.options?.find((o, i) => {
      if (o.strike == selectedData[ind].strike) {
        return o;
      }
    });
    if (newRow != undefined && newRow != null) {
      if (selectedData[ind].isCALL) {
        tSymbol = newRow.call.trading_symbol;
        tCode = newRow.call.code;
      } else {
        tSymbol = newRow.put.trading_symbol;
        tCode = newRow.put.code;
      }
    }
    return [tSymbol, tCode];
  };

  const extractStrikeValues = (optionData) => {
    if (!optionData || !Array.isArray(optionData.options)) {
      return [];
    }
    return optionData.options?.map((options) => options?.strike);
  };

  if (currentRows.length === 0) {
    return <ExmptyBasket onAddClick={handleAddNewStrategyClick} />;
  }

  return (
    <div className="w-full bg-p400 my-3 rounded-md">
      <div className="w-full min-h-[220px]">
        <div className="flex items-center justify-between border-b border-n100 border-opacity-10 px-4 py-3 rounded-t-md mb-2">
          <span className="w-[26px] text-[10px] font-medium text-n500 text-center">
            B/S
          </span>
          <span className="text-[10px] font-medium text-n500 text-center">
            Expiry
          </span>
          <span className="text-[10px] font-medium text-n500 text-center">
            Strike
          </span>
          <span className="text-[10px] font-medium text-n500 text-center">
            Type
          </span>
          <span className="text-[10px] font-medium text-n500 text-center">
            Lots
          </span>
          <div className="flex items-center text-[10px] font-medium text-n500 text-center">
            <span>Price</span>
            <div
              className="ml-2 cursor-pointer"
              onClick={handlePriceRefreshClick}>
              <img
                src={refreshIcon}
                alt="refresh"
                className={rotate ? "animate-spin" : ""}
                width={10}
                height={10}
                style={{
                  filter: theme === "dark" ? "brightness(0) invert(1)" : "none",
                }}
              />
            </div>
          </div>
          <div className="cursor-pointer" onClick={handleDeleteAllRows}>
            <img
              src={theme === "dark" ? headerDeleteDarkIcon : headerDeleteIcon}
              alt="delete"
              width={20}
              height={20}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          {currentRows?.map((row, ind) => (
            <div key={ind} className={`${ind % 2 !== 0 ? "bg-n000" : ""}`}>
              {/* Main row content */}
              <div className="flex items-center justify-between text-center px-4 py-1">
                <div
                  className={`w-[26px] h-[26px] flex items-center justify-center rounded text-white text-[10px] font-bold cursor-pointer ${row.isBuy ? "bg-p150" : "bg-red"}`}
                  onClick={() => toggleBuySell(ind)}>
                  {row.isBuy ? "B" : "S"}
                </div>

                <div className="relative">
                  <ExpiryDropdown
                    expiry={row.expiry}
                    expiries={
                      row.isFuture
                        ? selectedSymbol.fut_expiries
                        : selectedSymbol.option_expiries
                    }
                    onSelect={(selected) => handleExpiryChange(ind, selected)}
                  />
                </div>

                <div
                  className={`flex items-center justify-between w-24 px-2 rounded py-[3px] 
    ${row.isFuture ? "bg-n300 opacity-50 cursor-not-allowed" : "border border-n300"}`}>
                  <span
                    className={`text-[14px] font-medium ${
                      row.isFuture ? "n400" : "text-p300 cursor-pointer"
                    }`}
                    onClick={() =>
                      !row.isFuture && handleStrikeShift(ind, "prev")
                    }>
                    -
                  </span>

                  <span
                    className={`text-[10px] font-medium ${
                      row.isFuture ? "n400" : "text-p300 cursor-pointer"
                    }`}>
                    {row.isFuture ? "NA" : row.strike}
                  </span>

                  <span
                    className={`text-[14px] font-medium ${
                      row.isFuture ? "n400" : "text-p300 cursor-pointer"
                    }`}
                    onClick={() =>
                      !row.isFuture && handleStrikeShift(ind, "next")
                    }>
                    +
                  </span>
                </div>

                <div
                  className={`flex items-center justify-center w-[26px] h-[26px] rounded border-[1.5px] text-[10px] font-medium cursor-pointer
                  ${
                    row.isFuture
                      ? "bg-n300 text-p300 border-transparent"
                      : row.isCALL
                        ? "border-p150 text-p300"
                        : "border-red text-p300"
                  }`}
                  onClick={() => !row.isFuture && toggleCallPut(ind)}>
                  {row.isFuture ? "FUT" : row.isCALL ? "CE" : "PE"}
                </div>

                <div className="relative">
                  <CustomDropdown
                    options={[...Array(15)].map((_, i) => i + 1)}
                    value={row.lotSize}
                    onChange={(val) => handleLotSizeChange(ind, val)}
                    isLots={true}
                  />
                </div>

                <div className="flex items-center">
                  <button
                    className="px-2 py-1 border border-n200 rounded-l text-[10px] font-medium min-w-7 text-p300"
                    onClick={() => handleToggleOrderType(ind)}>
                    {row.orderType === "L" ? "L" : "M"}
                  </button>
                  <input
                    type="text"
                    value={row.price || ""}
                    className={`w-16 px-1 py-1 border border-n200 rounded-r text-center bg-p400 text-[10px] font-medium text-p300 ${row.orderType !== "L" ? "opacity-50 cursor-not-allowed" : ""}`}
                    onChange={(e) => handlePriceChange(e, ind)}
                    disabled={row.orderType !== "L"}
                  />
                </div>

                <div className="relative dropdown-container">
                  <div
                    className="cursor-pointer p-1 text-n400 border border-n300 rounded bg-p400 shadow-sm"
                    onClick={() => toggleDropdown(ind)}>
                    <MoreHorizontal
                      className="w-4 h-4"
                      style={{
                        color: theme === "dark" ? "#ffffff" : "#000000",
                      }}
                    />
                  </div>

                  {/* Dropdown Menu */}
                  {dropdownOpen === ind && (
                    <div className="absolute right-0 top-8 z-50 bg-p400 border border-n300 rounded-md shadow-lg min-w-32">
                      {!orderTypes[ind] && (
                        <>
                          {/* <div
                            className="text-p300 px-3 py-2 text-sm cursor-pointer hover:bg-n100 border-b border-n300 "
                            onClick={() => handleOrderTypeChange(ind, "cover")}>
                            Cover order
                          </div>
                          <div
                            className="text-p300 px-3 py-2 text-sm cursor-pointer hover:bg-n100 border-b border-n300"
                            onClick={() =>
                              handleOrderTypeChange(ind, "bracket")
                            }>
                            Bracket order
                          </div> */}
                        </>
                      )}
                      {orderTypes[ind] && (
                        <div
                          className="px-3 py-2 text-sm cursor-pointer hover:bg-n100 border-b border-n200 text-red"
                          onClick={() => handleRemoveOrderType(ind)}>
                          Remove{" "}
                          {orderTypes[ind] === "cover" ? "Cover" : "Bracket"}
                        </div>
                      )}
                      <div
                        className="px-3 py-2 text-sm cursor-pointer hover:bg-n100 text-red"
                        onClick={() => {
                          handleDeleteRow(ind);
                          setDropdownOpen(null);
                        }}>
                        Delete
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Additional input fields row */}
              {(orderTypes[ind] === "cover" ||
                orderTypes[ind] === "bracket") && (
                <div className="flex items-center justify-center gap-4 px-4 py-2">
                  {orderTypes[ind] === "cover" && (
                    <div className="w-[120px]">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder={
                            inputModes[ind]?.stoploss === "percentage"
                              ? "Stoploss %"
                              : "Stoploss ₹"
                          }
                          value={row.stoploss || ""}
                          className="w-full h-8 px-2 py-1 pr-8 border border-n300 rounded bg-p400 text-sm font-medium text-p300"
                          onChange={(e) =>
                            handleStoplossChange(ind, e.target.value)
                          }
                        />
                        <div className="absolute right-0 top-0 flex flex-col">
                          <button
                            className={`px-1 py-0.5 text-[8px] font-medium rounded-t ${inputModes[ind]?.stoploss === "percentage" ? "bg-p150 text-white" : "bg-n200 text-p300"}`}
                            onClick={() =>
                              handleToggleInputMode(
                                ind,
                                "stoploss",
                                "percentage"
                              )
                            }>
                            %
                          </button>
                          <button
                            className={`px-1 py-0.5 text-[8px] font-medium rounded-b ${inputModes[ind]?.stoploss === "absolute" ? "bg-p150 text-white" : "bg-n200 text-p300"}`}
                            onClick={() =>
                              handleToggleInputMode(ind, "stoploss", "absolute")
                            }>
                            ₹
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {orderTypes[ind] === "bracket" && (
                    <>
                      <div className="w-[120px]">
                        <div className="relative">
                          <input
                            type="text"
                            placeholder={
                              inputModes[ind]?.target === "percentage"
                                ? "Target %"
                                : "Target ₹"
                            }
                            value={row.target || ""}
                            className="w-full h-8 px-2 py-1 pr-8 border border-n300 rounded bg-p400 text-sm font-medium text-p300"
                            onChange={(e) =>
                              handleTargetChange(ind, e.target.value)
                            }
                          />
                          <div className="absolute right-0 top-0 flex flex-col">
                            <button
                              className={`px-1 py-0.5 text-[8px] font-medium rounded-t ${inputModes[ind]?.target === "percentage" ? "bg-p150 text-white" : "bg-n200 text-p300"}`}
                              onClick={() =>
                                handleToggleInputMode(
                                  ind,
                                  "target",
                                  "percentage"
                                )
                              }>
                              %
                            </button>
                            <button
                              className={`px-1 py-0.5 text-[8px] font-medium rounded-b ${inputModes[ind]?.target === "absolute" ? "bg-p150 text-white" : "bg-n200 text-p300"}`}
                              onClick={() =>
                                handleToggleInputMode(ind, "target", "absolute")
                              }>
                              ₹
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="w-[120px]">
                        <div className="relative">
                          <input
                            type="text"
                            placeholder={
                              inputModes[ind]?.stoploss === "percentage"
                                ? "Stoploss %"
                                : "Stoploss ₹"
                            }
                            value={row.stoploss || ""}
                            className="w-full h-8 px-2 py-1 pr-8 border border-n300 rounded bg-p400 text-sm font-medium text-p300"
                            onChange={(e) =>
                              handleStoplossChange(ind, e.target.value)
                            }
                          />
                          <div className="absolute right-0 top-0 flex flex-col">
                            <button
                              className={`px-1 py-0.5 text-[8px] font-medium rounded-t ${inputModes[ind]?.stoploss === "percentage" ? "bg-p150 text-white" : "bg-n200 text-p300"}`}
                              onClick={() =>
                                handleToggleInputMode(
                                  ind,
                                  "stoploss",
                                  "percentage"
                                )
                              }>
                              %
                            </button>
                            <button
                              className={`px-1 py-0.5 text-[8px] font-medium rounded-b ${inputModes[ind]?.stoploss === "absolute" ? "bg-p150 text-white" : "bg-n200 text-p300"}`}
                              onClick={() =>
                                handleToggleInputMode(
                                  ind,
                                  "stoploss",
                                  "absolute"
                                )
                              }>
                              ₹
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}

          <div className="flex items-center justify-between bg-n000 text-center px-4 py-1 opacity-50">
            <div className="w-[26px] h-[26px] flex items-center justify-center rounded text-white text-[10px] font-bold bg-n300">
              B
            </div>

            <select className="px-6 py-[4px] text-[10px] border border-n300 bg-p400 text-p300 rounded font-medium">
              <option>Expiry</option>
            </select>

            <div className="flex items-center justify-between w-24 px-2 ml-1 border border-n300 rounded py-[3px]">
              <span className="text-p300 text-[14px] font-medium">-</span>
              <span className="text-p300 text-[10px] font-medium">Strike</span>
              <span className="text-p300 text-[14px] font-medium">+</span>
            </div>

            <div className="flex items-center justify-center w-[26px] h-[26px] rounded border text-[10px] font-medium text-n400 border-n200">
              CE
            </div>

            <select className="px-[6px] py-[4px] text-[10px] border border-n300 bg-p400 text-p300 rounded font-medium">
              <option>00</option>
            </select>

            <div className="flex items-center">
              <button className="px-2 py-1 border border-n200 rounded-l text-[10px] font-medium">
                M
              </button>
              <input
                type="text"
                className="w-16 px-1 py-1 border border-n200 rounded-r text-center bg-p400 text-[10px] font-medium"
                disabled
              />
            </div>

            <div
              className="cursor-pointer p-1 text-n400 border border-n300 rounded bg-p400 shadow-sm"
              onClick={triggerHandleAddNewRow}>
              <Plus className="w-4 h-4 text-p300" />
            </div>
          </div>
        </div>
      </div>

      <div className="  text-[14px] font-medium">
        <div className="flex justify-between items-center p-4 pb-2">
          <div className="flex gap-3">
            <p className="text-[12px] font-medium text-n500">Net Premium</p>
            <p className="text-[14px] font-medium text-p300">
              {computedData?.["premium"]}
            </p>
          </div>
          <div className="flex gap-3">
            <p className="text-[12px] font-medium text-n500">Charges</p>
            <p className="text-[14px] font-medium text-p300">₹{charges}</p>
          </div>
        </div>

        <BasketBottom
          showConfirmModal={showConfirmModal}
          strategyData={selectedData}
          allOptionsData={allOptionsData}
        />
      </div>
    </div>
  );
};

export default Basket;
